import React from 'react';
import type { ChecklistStatus, CheckType, StepStatus } from '@/types/operations';
import { StatusPill, Tag } from '@/components/ui';
import type { PillTone } from '@/components/ui';

interface StepStatusMeta {
  label: string;
  tone: PillTone;
}

export const STEP_STATUS_META: Record<StepStatus, StepStatusMeta> = {
  PASSED:   { label: 'Passed',   tone: 'positive' },
  FAILED:   { label: 'Failed',   tone: 'critical' },
  PENDING:  { label: 'Pending',  tone: 'warning' },
  EXEMPTED: { label: 'Exempted', tone: 'neutral' },
};

const CHECK_TYPE_LABELS: Record<CheckType, string> = {
  AUTO:   'Auto',
  MANUAL: 'Manual',
};

const CHECKLIST_STATUS_META: Record<ChecklistStatus, StepStatusMeta> = {
  OPEN:      { label: 'Open',      tone: 'neutral' },
  COMPLETED: { label: 'Completed', tone: 'positive' },
};

const CATEGORY_LABELS: Record<string, string> = {
  DATA_AVAILABILITY: 'Data Availability',
  DATA_INTEGRITY:    'Data Integrity',
  CONTRACT_NOTES:    'Contract Notes',
  ORDERS:            'Orders',
};

const toTitleCase = (value: string): string =>
  value
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const formatCategoryLabel = (category: string): string =>
  CATEGORY_LABELS[category] ?? (category ? toTitleCase(category) : 'Other');

export const StepStatusBadge: React.FC<{ status: StepStatus }> = ({ status }) => (
  <StatusPill value={status} meta={STEP_STATUS_META} fallback={STEP_STATUS_META.PENDING} />
);

export const ChecklistStatusBadge: React.FC<{ status: ChecklistStatus }> = ({ status }) => (
  <StatusPill value={status} meta={CHECKLIST_STATUS_META} fallback={CHECKLIST_STATUS_META.OPEN} />
);

export const CheckTypeBadge: React.FC<{ checkType: CheckType }> = ({ checkType }) => (
  <Tag>{CHECK_TYPE_LABELS[checkType] ?? CHECK_TYPE_LABELS.AUTO}</Tag>
);
